
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";

export function useFileCompletionMarker(resourceId?: string, fileId?: string) {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isFirebaseError, setIsFirebaseError] = useState(false);

  const markAsCompleted = useCallback(async () => {
    if (!resourceId || !fileId || !session?.user?.id) {
      return Promise.reject(new Error('Missing required parameters'));
    }
    
    try {
      setIsLoading(true);
      setError(null);
      setIsFirebaseError(false);
      
      const now = new Date().toISOString();
      
      console.log(`Marking file ${fileId} in resource ${resourceId} as completed`);

      const { data: existingProgress } = await supabase
        .from('training_progress')
        .select('id')
        .eq('resource_id', resourceId)
        .eq('file_id', fileId)
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (existingProgress) {
        const { error: updateError } = await supabase
          .from('training_progress')
          .update({
            completed: true,
            completed_at: now,
            updated_at: now
          })
          .eq('id', existingProgress.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('training_progress')
          .insert({
            resource_id: resourceId,
            file_id: fileId,
            user_id: session.user.id,
            completed: true,
            completed_at: now,
            updated_at: now
          });

        if (insertError) throw insertError;
      }
      
      return Promise.resolve();
      
    } catch (error) {
      console.error('Error in markAsCompleted:', error);
      
      const errorString = String(error);
      if (
        errorString.includes('firebase') || 
        errorString.includes('firestore') || 
        errorString.includes('400') || 
        errorString.includes('Failed to fetch')
      ) {
        setIsFirebaseError(true);
      }
      
      setError(error instanceof Error ? error : new Error('Unknown error in markAsCompleted'));
      return Promise.reject(error);
    } finally {
      setIsLoading(false);
    }
  }, [resourceId, fileId, session?.user?.id]);

  return {
    isLoading,
    error,
    isFirebaseError,
    markAsCompleted
  };
}
